import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { isResponsive } from '../../../../preview';

import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

import { useFormik } from 'formik';

import { Props, Fields } from './types';
import {
  ENERGY_TYPE_TRANSLATIONS,
  GEARBOX_TYPE_TRANSLATIONS,
  LOT_LIMIT_OPTIONS,
  SOLD_DAYS_AGO_OPTIONS,
  ANY_OPTION,
  FIELDS_TO_LABELS,
  LOTS_LIMIT_ALL_OPTION,
  LOTS_LIMIT_ALL_VALUE,
  isBrokenCarsSale,
} from './constants';

import { FilterQuery } from '../../types';

const Filter: FC<Props> = ({ data, onSubmit, isLoading }) => {
  const [expanded, setExpanded] = useState(false);
  const formik = useFormik<Partial<FilterQuery>>({
    initialValues: {
      [Fields.CarName]: '',
      [Fields.CarModel]: '',
      [Fields.GearboxType]: '',
      [Fields.EnergyType]: '',
      [Fields.MinRegistrationYear]: '',
      [Fields.MaxRegistrationYear]: '',
      [Fields.MinMileage]: '',
      [Fields.MaxMileage]: '',
      [Fields.LotNumber]: '',
      [Fields.LotsLimit]: 100,
      [Fields.OnlySold]: false,
      [Fields.MaxSoldDaysAgo]: 90,
      [Fields.SaleId]: '',
      [Fields.ExcludeHS]: false,
    },
    onSubmit: () => {
      const values = { ...formik.values };

      if (values.lotsLimit === LOTS_LIMIT_ALL_VALUE) {
        delete values.lotsLimit;
      }

      onSubmit(values);
    },
  });

  const renderAutocompleteInput = useCallback((params: AutocompleteRenderInputParams) => {
    const { id } = params;
    return <TextField {...params} label={FIELDS_TO_LABELS[id as Fields] ?? id} />;
  }, []);

  const handleAutocompleteChange = useCallback(
    (
      id: Fields,
      event: React.SyntheticEvent<Element, Event>,
      value: string | null,
      reason?: string,
    ) => {
      formik.setFieldValue(id, value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      formik.setFieldValue(event.currentTarget.id, checked);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const carNameOptions = useMemo(
    () => (data ? data.lots.map(({ name }) => name) : []),
    [data],
  );

  const carModelOptions = useMemo(
    () =>
      (data && data.lots.find(({ name }) => name === formik.values.carName)?.models) || [],
    [data, formik.values.carName],
  );

  const gearboxTypeOptions = useMemo(
    () => [
      <MenuItem key={ANY_OPTION.value} value={ANY_OPTION.value}>
        {ANY_OPTION.label}
      </MenuItem>,
      ...(data
        ? data.gearboxTypes.map((gearboxType) => (
            <MenuItem key={gearboxType} value={gearboxType}>
              {GEARBOX_TYPE_TRANSLATIONS[gearboxType]}
            </MenuItem>
          ))
        : []),
    ],
    [data],
  );

  const energyTypeOptions = useMemo(
    () => [
      <MenuItem key={ANY_OPTION.value} value={ANY_OPTION.value}>
        {ANY_OPTION.maleLabel}
      </MenuItem>,
      ...(data
        ? data.energyTypes.map((energyType) => (
            <MenuItem key={energyType} value={energyType}>
              {ENERGY_TYPE_TRANSLATIONS[energyType]}
            </MenuItem>
          ))
        : []),
    ],
    [data],
  );

  const maxSoldDaysAgoOptions = useMemo(
    () =>
      SOLD_DAYS_AGO_OPTIONS.map((soldDaysAgo) => (
        <MenuItem key={soldDaysAgo.value} value={soldDaysAgo.value}>
          {soldDaysAgo.label}
        </MenuItem>
      )),
    [],
  );

  const lotsLimitOptions = useMemo(
    () =>
      [
        ...(formik.values.saleId ? [LOTS_LIMIT_ALL_OPTION] : []),
        ...LOT_LIMIT_OPTIONS,
      ].map(({ label, value }) => (
        <MenuItem key={value} value={value}>
          {label}
        </MenuItem>
      )),
    [formik.values.saleId],
  );

  const saleIdOptions = useMemo(() => {
    let sales: string[];

    if (!data?.sales) {
      sales = [];
    } else if (!formik.values.excludeHS) {
      sales = data.sales;
    } else {
      sales = data.sales.filter((saleId) => !isBrokenCarsSale(saleId));
    }

    return [
      <MenuItem key={ANY_OPTION.value} value={ANY_OPTION.value}>
        {ANY_OPTION.maleLabel}
      </MenuItem>,
      ...sales.map((saleId) => (
        <MenuItem key={saleId} value={saleId}>
          {saleId}
        </MenuItem>
      )),
    ];
  }, [data?.sales, formik.values.excludeHS]);

  useEffect(() => {
    if (formik.values.saleId && isBrokenCarsSale(formik.values.saleId) && formik.values.excludeHS) {
      formik.setFieldValue(Fields.SaleId, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.excludeHS]);

  useEffect(() => {
    if (!formik.values.saleId && formik.values.lotsLimit === LOTS_LIMIT_ALL_VALUE) {
      formik.setFieldValue(Fields.LotsLimit, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.saleId]);

  return (
    <Paper elevation={2} className={`filter-panel ${expanded ? 'filter-expanded' : ''}`}>
      <Box padding={3} position="relative" className="filter-content" aria-busy={isLoading}>
        {isLoading && (
          <Box
            position="absolute"
            top={0}
            bottom={0}
            left={0}
            right={0}
            justifyContent="center"
            alignItems="center"
            display="flex"
          >
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                width: '100%',
                height: '100%',
                position: 'absolute',
                zIndex: 10,
              }}
            />
            <CircularProgress
              size={60}
              sx={{
                position: 'relative',
                zIndex: 11,
              }}
            />
          </Box>
        )}

        {isResponsive && (
          <Button className="filter-toggle" aria-expanded={expanded}
            aria-controls="carModel filter-specs-gearbox filter-specs-energy filter-summary-options filter-exclude-hs filter-sale filter-lot"
            onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Свернуть фильтры' : 'Все фильтры'}
          </Button>
        )}
        <Stack className="filter-main" direction="row" justifyContent="space-between" gap={2} mb={2}>
          <Stack width="40%" rowGap={1.5}>
            <Autocomplete
              className="filter-car-name"
              size="small"
              id={Fields.CarName}
              freeSolo
              value={formik.values[Fields.CarName]}
              onInputChange={(event, value, reason) =>
                handleAutocompleteChange(Fields.CarName, event, value, reason)
              }
              renderInput={renderAutocompleteInput}
              options={carNameOptions}
            />

            <Autocomplete
              className="filter-model"
              size="small"
              id={Fields.CarModel}
              freeSolo
              value={formik.values[Fields.CarModel]}
              onInputChange={(event, value, reason) =>
                handleAutocompleteChange(Fields.CarModel, event, value, reason)
              }
              renderInput={renderAutocompleteInput}
              options={carModelOptions}
            />
          </Stack>

          <Stack id="filter-specs-gearbox" className="filter-specs" width="30%" rowGap={1.5}>
            <FormControl size="small">
              <InputLabel id="gearbox-type-label">
                {FIELDS_TO_LABELS[Fields.GearboxType]}
              </InputLabel>

              <Select
                labelId="gearbox-type-label"
                value={formik.values[Fields.GearboxType]}
                label={FIELDS_TO_LABELS[Fields.GearboxType]}
                name={Fields.GearboxType}
                onChange={formik.handleChange}
              >
                {gearboxTypeOptions}
              </Select>
            </FormControl>

            <Stack direction="row">
              <TextField
                size="small"
                label={FIELDS_TO_LABELS[Fields.MinRegistrationYear]}
                value={formik.values[Fields.MinRegistrationYear]}
                name={Fields.MinRegistrationYear}
                onChange={formik.handleChange}
                InputProps={{
                  sx: {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
              />
              <TextField
                size="small"
                label={FIELDS_TO_LABELS[Fields.MaxRegistrationYear]}
                value={formik.values[Fields.MaxRegistrationYear]}
                name={Fields.MaxRegistrationYear}
                onChange={formik.handleChange}
                sx={{ marginLeft: '-1px' }}
                InputProps={{
                  sx: {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                }}
              />
            </Stack>
          </Stack>

          <Stack id="filter-specs-energy" className="filter-specs" width="30%" rowGap={1.5}>
            <FormControl size="small">
              <InputLabel id="energy-type-label">
                {FIELDS_TO_LABELS[Fields.EnergyType]}
              </InputLabel>

              <Select
                labelId="energy-type-label"
                value={formik.values[Fields.EnergyType]}
                label={FIELDS_TO_LABELS[Fields.EnergyType]}
                name={Fields.EnergyType}
                onChange={formik.handleChange}
              >
                {energyTypeOptions}
              </Select>
            </FormControl>

            <Stack direction="row">
              <TextField
                size="small"
                label={FIELDS_TO_LABELS[Fields.MinMileage]}
                value={formik.values[Fields.MinMileage]}
                name={Fields.MinMileage}
                onChange={formik.handleChange}
                InputProps={{
                  sx: {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
              />
              <TextField
                size="small"
                label={FIELDS_TO_LABELS[Fields.MaxMileage]}
                value={formik.values[Fields.MaxMileage]}
                name={Fields.MaxMileage}
                onChange={formik.handleChange}
                sx={{ marginLeft: '-1px' }}
                InputProps={{
                  sx: {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                }}
              />
            </Stack>
          </Stack>
        </Stack>

        <Stack id="filter-options" className="filter-options" mt={2} direction="row" gap={2} alignItems="center">
          <Stack id="filter-summary-options" className="filter-summary-options" width="38.8%" direction="row" gap={2}>
            <FormControl sx={{ width: '50%' }} size="small">
              <InputLabel id="lots-limit-label">
                {FIELDS_TO_LABELS[Fields.LotsLimit]}
              </InputLabel>
              <Select
                labelId="lots-limit-label"
                value={formik.values[Fields.LotsLimit]}
                label={FIELDS_TO_LABELS[Fields.LotsLimit]}
                name={Fields.LotsLimit}
                onChange={formik.handleChange}
              >
                {lotsLimitOptions}
              </Select>
            </FormControl>

            <FormControl sx={{ width: '50%' }} size="small">
              <InputLabel id="max-sold-days-ago-label">
                {FIELDS_TO_LABELS[Fields.MaxSoldDaysAgo]}
              </InputLabel>

              <Select
                labelId="max-sold-days-ago-label"
                value={formik.values[Fields.MaxSoldDaysAgo]}
                label={FIELDS_TO_LABELS[Fields.MaxSoldDaysAgo]}
                name={Fields.MaxSoldDaysAgo}
                onChange={formik.handleChange}
              >
                {maxSoldDaysAgoOptions}
              </Select>
            </FormControl>
          </Stack>

          <FormControl id="filter-exclude-hs" className="filter-exclude-hs" sx={{ width: '40%' }} size="small">
            <FormControlLabel
              control={
                <Checkbox
                  id={Fields.ExcludeHS}
                  checked={formik.values[Fields.ExcludeHS]}
                  name={Fields.ExcludeHS}
                  onChange={handleCheckboxChange}
                />
              }
              label={FIELDS_TO_LABELS[Fields.ExcludeHS]}
              componentsProps={{ typography: { variant: 'body2' } }}
            />
          </FormControl>
        </Stack>

        <Divider sx={{ mt: 2 }} />

        <Stack className="filter-bottom" mt={2} direction="row" gap={2} alignItems="center">
          <FormControl id="filter-sale" className="filter-sale" size="small" sx={{ width: '40%' }}>
            <InputLabel id="sale-id-label">{FIELDS_TO_LABELS[Fields.SaleId]}</InputLabel>

            <Select
              labelId="sale-id-label"
              value={formik.values[Fields.SaleId]}
              label={FIELDS_TO_LABELS[Fields.SaleId]}
              name={Fields.SaleId}
              onChange={formik.handleChange}
            >
              {saleIdOptions}
            </Select>
          </FormControl>

          <FormControl id="filter-lot" className="filter-lot" sx={{ width: '20%' }} size="small">
            <TextField
              size="small"
              label={FIELDS_TO_LABELS[Fields.LotNumber]}
              value={formik.values[Fields.LotNumber]}
              name={Fields.LotNumber}
              onChange={formik.handleChange}
            />
          </FormControl>

          <Stack className="filter-actions" justifyContent="flex-end" direction="row" gap={3} flex={1}>
            <Button variant="outlined" onClick={() => formik.resetForm()}>
              {'\u041e\u0427\u0418\u0421\u0422\u0418\u0422\u042c'}
            </Button>

            <Button disabled={isLoading} variant="contained" onClick={() => formik.handleSubmit()}>
              {'\u041f\u041e\u041a\u0410\u0417\u0410\u0422\u042c \u0420\u0415\u0417\u0423\u041b\u042c\u0422\u0410\u0422\u042b'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

export { Filter };
